// src/pages/Dashboard.js
import { useState, useEffect } from "react";
import { Layout, Spin } from "antd";
import { useVisibilityTracking } from "../hooks/useVisibilityTracking";
import { Header } from "../components/layout/header";
import { AppointmentList } from "../components/features/appointments/AppointmentList";
import { AppointmentHeader } from "../components/features/appointments/AppointmentHeader";
import { getAppointments } from "../services/api";
import { useMsal } from "@azure/msal-react";

const { Content } = Layout;

function extractTimeWithoutSeconds(dateStr) {
  const iso = (dateStr || "").replace(" ", "T");
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Invalid Time";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export default function Dashboard({
  selectedDoctorId,
  setSelectedDoctorId,
  selectedDate,
  setSelectedDate,
  options
}) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const dashboardContentRef = useVisibilityTracking('dashboard-appointments');

  const { instance } = useMsal();

  const logout = () => {
    instance.logout();
    sessionStorage.clear();
  };

  useEffect(() => {
    let alive = true;
    const fetchAppointments = async () => {
      setLoading(true);
      try {
        const resp = await getAppointments({
          clinicianId: selectedDoctorId,
          startDate: selectedDate,
        });
        const arr = Array.isArray(resp?.data) ? resp.data : [];
        const mod = arr.map((x) => ({
          ...x,
          id: x?.patient_id,
          time: extractTimeWithoutSeconds(x?.session_date),
          patientName: `${x?.patient_firstname || ""} ${
            x?.patient_lastname || ""
          }`.trim(),
          valid_appt_flag: x?.valid_appt_flag ?? true,
        }));
        if (alive) setAppointments(mod);
      } catch (e) {
        console.error("Error fetching appointments:", e);
        if (alive) setAppointments([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchAppointments();
    return () => {
      alive = false;
    };
  }, [selectedDoctorId, selectedDate]);

  const filterAppointmentsByTime = (timeOfDay) => {
    if (timeOfDay === "morning") {
      return appointments.filter(
        (a) => a.time.includes("AM") && parseInt(a.time.split(":")[0]) <= 12
      );
    } else if (timeOfDay === "afternoon") {
      return appointments.filter(
        (a) =>
          (a.time.includes("PM") && parseInt(a.time.split(":")[0]) < 5) ||
          (a.time.includes("PM") && parseInt(a.time.split(":")[0]) === 12)
      );
    } else {
      return appointments.filter(
        (a) =>
          a.time.includes("PM") &&
          parseInt(a.time.split(":")[0]) >= 5 &&
          parseInt(a.time.split(":")[0]) < 12
      );
    }
  };

  return (
    <Layout className="flex flex-col items-center h-screen">
      <Header
        onLogout={logout}
        selectedDoctorId={selectedDoctorId}
        onSelectDoctor={setSelectedDoctorId}
        options={options}
      />
      <Content ref={dashboardContentRef} className="w-full flex flex-col items-center py-10 overflow-y-auto">
        <AppointmentHeader
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
            <p className="mt-4 text-gray-500 text-sm font-medium">Loading appointments...</p>
          </div>
        ) : (
          <>
            <AppointmentList
              title="Morning"
              timeOfDay="morning"
              appointments={filterAppointmentsByTime("morning")}
            />
            <AppointmentList
              title="Afternoon"
              timeOfDay="afternoon"
              appointments={filterAppointmentsByTime("afternoon")}
            />
            <AppointmentList
              title="Evening"
              timeOfDay="evening"
              appointments={filterAppointmentsByTime("evening")}
            />
          </>
        )}
      </Content>
    </Layout>
  );
}
